import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DataPageLayout from '../components/DataPageLayout';
import ImportPreviewModal from '../components/ImportPreviewModal';
import {
  addVessel,
  deleteVessels,
  importVessels,
  listVessels,
  verifyImportVessels,
  updateVessel,
} from '../services/vesselService';
import './Dashboard.css';

const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024;

function formatCount(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

function Vessels() {
  const fileInputRef = useRef(null);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);

  const loadVessels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listVessels();
      setVessels(data);
      setError('');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setVessels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVessels();
  }, [loadVessels]);

  const uniqueDomains = useMemo(() => {
    const domains = vessels
      .map((vessel) => vessel.email?.split('@')[1])
      .filter(Boolean);
    return new Set(domains).size;
  }, [vessels]);

  const metrics = [
    {
      title: 'Total Vessels',
      value: loading ? '...' : formatCount(vessels.length),
      subtext: 'Vessels in your list',
      highlight: true,
    },
    {
      title: 'Unique Domains',
      value: loading ? '...' : formatCount(uniqueDomains),
      subtext: 'Different email domains',
    },
    {
      title: 'Reserved',
      value: '—',
      subtext: 'Coming soon',
    },
    {
      title: 'Reserved',
      value: '—',
      subtext: 'Coming soon',
    },
  ];

  const handleImportClick = () => {
    setImportMessage('');
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported.');
      return;
    }

    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.error('File is too large. Maximum size is 10MB.');
      return;
    }

    setImportLoading(true);
    setImportMessage('');
    setError('');
    setPreviewLoading(true);
    setPendingImportFile(file);
    setImportPreview({ rows: [], total: 0, new: 0, exists: 0, invalid: 0 });

    try {
      const preview = await verifyImportVessels(file);
      setImportPreview(preview);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setPendingImportFile(null);
      setImportPreview(null);
    } finally {
      setImportLoading(false);
      setPreviewLoading(false);
    }
  };

  const handleCloseImportPreview = () => {
    if (confirmingImport) {
      return;
    }

    setPendingImportFile(null);
    setImportPreview(null);
    setPreviewLoading(false);
  };

  const handleConfirmImport = async () => {
    if (!pendingImportFile) {
      return;
    }

    setConfirmingImport(true);
    setError('');

    try {
      const result = await importVessels(pendingImportFile);
      const message = `Imported ${result.inserted} vessel(s).`;
      setImportMessage(message);
      setPendingImportFile(null);
      setImportPreview(null);
      await loadVessels();
      toast.success(message);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setConfirmingImport(false);
    }
  };

  const importPreviewColumns = [
    { key: 'name', label: 'Vessel Name' },
    { key: 'email', label: 'Email' },
  ];

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Vessel Name', className: 'data-page__cell-truncate' },
    { key: 'email', label: 'Email', className: 'data-page__cell-truncate' },
    {
      key: 'created_at',
      label: 'Added At',
      render: (row) => formatDate(row.created_at),
    },
  ];

  const vesselFields = [
    { key: 'name', label: 'Vessel Name' },
    { key: 'email', label: 'Email', type: 'email', validate: 'email' },
  ];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportFile}
        hidden
      />
      <DataPageLayout
        title="Vessels"
        subtitle="Vessels and the emails used to reach them."
        metrics={metrics}
        loading={loading}
        error={error}
        successMessage={importMessage}
        onRefresh={loadVessels}
        importLabel="Import Vessels"
        onImportClick={handleImportClick}
        importLoading={importLoading}
        addLabel="Add Vessel"
        addFields={vesselFields}
        onSaveAdd={async (values) => {
          try {
            await addVessel({
              name: values.name.trim(),
              email: values.email.trim(),
            });
            setError('');
            setImportMessage('Vessel added successfully.');
            await loadVessels();
            toast.success('Vessel added successfully.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        columns={columns}
        rows={vessels}
        searchable
        searchPlaceholder="Search name or email..."
        searchKeys={['name', 'email']}
        emptyMessage="No vessels found."
        editFields={vesselFields}
        onSaveEdit={async (row, values) => {
          try {
            await updateVessel(row.id, {
              name: values.name.trim(),
              email: values.email.trim(),
            });
            await loadVessels();
            toast.success('Vessel updated successfully.');
          } catch (err) {
            toast.error(err.message);
            throw err;
          }
        }}
        onDelete={async (row) => {
          try {
            await deleteVessels([row.id]);
            await loadVessels();
            setError('');
            toast.success('Vessel deleted successfully.');
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteMany={async (selected) => {
          try {
            await deleteVessels(selected.map((row) => row.id));
            await loadVessels();
            setError('');
            toast.success(`Deleted ${selected.length} vessel(s).`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        onDeleteAll={async (allRows) => {
          try {
            await deleteVessels(allRows.map((row) => row.id));
            await loadVessels();
            setError('');
            toast.success(`Deleted all ${allRows.length} vessel(s).`);
          } catch (err) {
            setError(err.message);
            toast.error(err.message);
            throw err;
          }
        }}
        deleteConfirmMessage="Delete this vessel?"
        deleteManyConfirmMessage="Delete selected vessels?"
        deleteAllConfirmMessage="Delete all vessels?"
      />

      {importPreview && (
        <ImportPreviewModal
          title="Import Vessels Preview"
          fileName={pendingImportFile?.name}
          columns={importPreviewColumns}
          rows={importPreview.rows}
          total={importPreview.total}
          newCount={importPreview.new}
          existsCount={importPreview.exists}
          invalidCount={importPreview.invalid}
          loading={previewLoading}
          confirming={confirmingImport}
          onClose={handleCloseImportPreview}
          onConfirm={handleConfirmImport}
        />
      )}
    </>
  );
}

export default Vessels;
